import { Injectable, inject, DestroyRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, combineLatest, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthHubService } from './auth-hub.service';
import { AuthService } from '@auth0/auth0-angular';

@Injectable({
  providedIn: 'root'
})
export class AuthorizationMonitorService {

  private readonly allowedRoutes = ['/unauthorized'];

  private readonly authHub = inject(AuthHubService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.initializeMonitoring();
  }

  private initializeMonitoring() {
    // Monitora ogni cambio di rotta
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((event: NavigationEnd) => {
      this.checkAuthorizationForRoute(event.url);
    });

    // Monitora i cambiamenti dello stato di autorizzazione
    combineLatest([
      this.auth.isAuthenticated$,
      this.authHub.isAuthorized$,
      this.auth.isLoading$
    ]).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(([isAuthenticated, isAuthorized, isLoading]) => {
      if (!isLoading) {
        this.handleAuthorizationChange(isAuthenticated, isAuthorized);
      }
    });
  }

  private checkAuthorizationForRoute(url: string) {
    // Se l'utente è su una rotta non autorizzata, verifica i permessi
    if (!this.allowedRoutes.some(route => url.includes(route))) {
      combineLatest([
        this.auth.isAuthenticated$,
        this.authHub.isAuthorized$,
        this.auth.isLoading$
      ]).pipe(take(1)).subscribe(([isAuthenticated, isAuthorized, isLoading]) => {
        if (!isLoading && (!isAuthenticated || !isAuthorized)) {
          console.warn('Accesso non autorizzato alla rotta:', url);
          this.redirectToUnauthorized();
        }
      });
    }
  }

  private handleAuthorizationChange(isAuthenticated: boolean, isAuthorized: boolean) {
    const currentUrl = this.router.url;
    
    // Se l'utente non è autenticato, reindirizza al login
    if (!isAuthenticated) {
      console.log('Utente non autenticato - reindirizzamento al login');
      this.auth.loginWithRedirect();
      return;
    }
    
    // Se l'utente è autenticato ma non autorizzato
    if (isAuthenticated && !isAuthorized) {
      if (!this.allowedRoutes.some(route => currentUrl.includes(route))) {
        console.warn('Utente non autorizzato - reindirizzamento a /unauthorized');
        this.redirectToUnauthorized();
      }
      return;
    }
    
    // Se l'utente è sia autenticato che autorizzato ma è nella pagina unauthorized
    if (isAuthenticated && isAuthorized && currentUrl.includes('/unauthorized')) {
      console.log('Utente autorizzato - reindirizzamento alla home');
      this.router.navigate(['/activate']);
    }
  }

  private redirectToUnauthorized() {
    this.router.navigate(['/unauthorized']);
  }

  // Metodo per verificare se una rotta è accessibile
  isRouteAllowed(route: string): boolean {
    return this.allowedRoutes.some(allowedRoute => route.includes(allowedRoute));
  }
}